import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { CalendarClock, Handshake, UserCog, Users } from "lucide-react";

import { listRows, listRegistrations, listSettings, saveSetting } from "@/lib/api";
import { AdminPage, Loading } from "@/components/admin/AdminKit";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";

export const Route = createFileRoute("/_authenticated/admin/")({
  component: Dashboard,
});

function Dashboard() {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "dashboard"],
    queryFn: async () => {
      const [regs, speakers, partners, agenda, settings] = await Promise.all([
        listRegistrations(),
        listRows("speakers"),
        listRows("partners"),
        listRows("agenda_items"),
        listSettings(),
      ]);
      return {
        registrations: regs.length,
        speakers: speakers.length,
        partners: partners.length,
        agenda: agenda.length,
        open: settings.find((s) => s.key === "registrations_open")?.value === "true",
      };
    },
  });

  async function toggleRegistrations(next: boolean) {
    try {
      await saveSetting("registrations_open", next ? "true" : "false");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Eroare");
      return;
    }
    toast.success(next ? "Înregistrările sunt deschise" : "Înregistrările sunt oprite");
    await queryClient.invalidateQueries({ queryKey: ["admin", "dashboard"] });
  }

  if (isLoading || !data) return <Loading />;

  const cards = [
    { label: "Înscrieri", value: data.registrations, icon: Users, to: "/admin/inscrieri" },
    { label: "Speakeri", value: data.speakers, icon: UserCog, to: "/admin/speakeri" },
    { label: "Parteneri", value: data.partners, icon: Handshake, to: "/admin/parteneri" },
    { label: "Puncte agendă", value: data.agenda, icon: CalendarClock, to: "/admin/agenda" },
  ] as const;

  return (
    <AdminPage title="Dashboard" description="Privire de ansamblu asupra evenimentului.">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {cards.map((card) => {
          const Icon = card.icon;
          return (
            <Link
              key={card.label}
              to={card.to}
              className="group rounded-xl border border-border bg-card p-5 transition-all hover:-translate-y-0.5 hover:shadow-md"
            >
              <Icon className="h-5 w-5 text-energy" />
              <p className="mt-4 text-3xl font-bold">{card.value}</p>
              <p className="text-sm text-muted-foreground">{card.label}</p>
            </Link>
          );
        })}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-4 rounded-xl border border-border bg-card p-5">
        <div>
          <Label htmlFor="reg-toggle" className="text-base">
            Înregistrări deschise
          </Label>
          <p className="text-sm text-muted-foreground">
            Când e oprit, formularul public nu mai acceptă înscrieri.
          </p>
        </div>
        <Switch id="reg-toggle" checked={data.open} onCheckedChange={toggleRegistrations} />
      </div>
    </AdminPage>
  );
}
